import React, { Suspense, lazy, useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/MobileHeader';
import MobileChatHeader from '@/components/MobileChatHeader';
import MobileMenu from '@/components/MobileMenu';

import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ChatProvider, useChat } from '@/contexts/ChatContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { logger } from '@/utils/logger';

// Lazy load pages
const HomePage = lazy(() => import('@/pages/HomePage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const SignupPage = lazy(() => import('@/pages/SignupPage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const ChatPage = lazy(() => import('@/pages/ChatPage'));
const UserDashboardPage = lazy(() => import('@/pages/UserDashboardPage'));
const AdminDashboardPage = lazy(() => import('@/pages/AdminDashboardPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const FAQPage = lazy(() => import('@/pages/FAQPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const CookiesPage = lazy(() => import('@/pages/CookiesPage'));
const DisclaimerPage = lazy(() => import('@/pages/DisclaimerPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const AuthCallbackPage = lazy(() => import('@/pages/AuthCallbackPage'));
const CheckoutSuccessPage = lazy(() => import('@/pages/CheckoutSuccessPage'));
const CheckoutCancelPage = lazy(() => import('@/pages/CheckoutCancelPage'));
const MedicalPage = lazy(() => import('@/pages/MedicalPage'));
const LegalPage = lazy(() => import('@/pages/LegalPage'));
const FashionPage = lazy(() => import('@/pages/FashionPage'));

// Protected route wrapper
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireAdmin = false,
}) => {
  const { user, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (requireAuth && !user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !userProfile?.is_admin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// In App.tsx
const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { authVersion } = useAuth(); // Add this to track auth state changes
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isChatRoute = location.pathname.startsWith('/chat');

  // Check if we're in development environment
  const isDevelopment = import.meta.env.VITE_ENVIRONMENT === 'development';

  // Toggle for mobile menu visibility
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Reset mobile menu state when auth changes
  useEffect(() => {
    logger.debug('AppContent', `Auth version changed to ${authVersion}, resetting mobile menu state`);
    setShowMobileMenu(false);
  }, [authVersion]);

  const {
    conversations,
    currentConversation,
    createConversation,
  } = useChat();

  // Handlers for mobile chat header
  const handleStartNewChat = async () => {
    const newConversation = await createConversation();
    if (newConversation) {
      navigate(`/chat/${newConversation.id}`);
      setShowMobileMenu(false);
    }
  };

  const handleSelectConversation = (id: string) => {
    navigate(`/chat/${id}`);
    setShowMobileMenu(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      {isMobile ? (
        isChatRoute ? (
          <MobileChatHeader
            currentConversation={currentConversation}
            conversations={conversations}
            onSelectConversation={handleSelectConversation}
            onNewChat={handleStartNewChat}
            onMenuClick={() => setShowMobileMenu(true)}
          />
        ) : (
          <MobileHeader
            isMenuOpen={showMobileMenu}
            toggleMenu={() => setShowMobileMenu(prev => !prev)}
          />
        )
      ) : (
        <Navigation />
      )}

      {/* Mobile Menu using Portal - completely independent rendering */}
      {showMobileMenu && (
        <MobileMenu
          onClose={() => setShowMobileMenu(false)}
        />
      )}

      {/* Main content */}
      <Suspense
        fallback={
          <div className="flex justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
          </div>
        }
      >
        <div className="flex-grow container mx-auto px-4 py-8">
          <Routes>
            {/* Main routes available in all environments */}
            <Route path="/" element={<HomePage />} />
            
            {/* Other routes available in all environments */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

           {/* Placeholder pages for carousel items */}
           <Route path="/medical" element={<MedicalPage />} />
           <Route path="/legal" element={<LegalPage />} />
           <Route path="/fashion" element={<FashionPage />} />

            <Route
              path="/checkout/success"
              element={
                <ProtectedRoute>
                  <CheckoutSuccessPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout/cancel"
              element={
                <ProtectedRoute>
                  <CheckoutCancelPage />
                </ProtectedRoute>
              }
            />

            {/* Chat routes */}
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/chat/:conversationId" element={<ChatPage />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <UserDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute requireAuth requireAdmin>
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* 404 fallback */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
      </Suspense>

      {/* Footer (hidden on mobile + chat) */}
      {!isChatRoute && <Footer />}
    </div>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    if (!localStorage.getItem('anonymous_session_id')) {
      localStorage.setItem('anonymous_session_id', crypto.randomUUID());
    }
  }, []);

  return (
    <AuthProvider>
      <ChatProvider>
        <Router>
          <AppContent />
        </Router>
      </ChatProvider>
    </AuthProvider>
  );
};

export default App;